import {
	addEventProcessor,
	type Breadcrumb,
	consoleLoggingIntegration,
	type Event,
	feedbackIntegration,
	init,
	reactErrorHandler,
	replayIntegration,
	showReportDialog,
	tanstackRouterBrowserTracingIntegration,
} from "@sentry/react";
import { withoutSetPasswordToken } from "@/lib/set-password-token";

export const SENTRY_ENVIRONMENT =
	import.meta.env.VITE_SENTRY_ENVIRONMENT || "local";
export const SENTRY_ENABLED = SENTRY_ENVIRONMENT !== "local";

export type SentryConfig = {
	dsn: string;
	tunnel?: string;
};

/*
 * Sentry never carries a set-password link's token. `main.tsx`
 * takes it off the address before Sentry starts, but a second link opened in a
 * tab already on the page changes only the fragment, and Sentry hears that, and
 * the `replaceState` that takes it off again: as navigation breadcrumbs, in a
 * replay's navigation spans and its list of addresses, and in the address of
 * anything reported meanwhile. The page load's own timing spans name the
 * address the browser opened, which `replaceState` never rewrites
 * (https://w3c.github.io/navigation-timing/#dom-performancenavigationtiming).
 * Each of those passes through one of these.
 */

const ADDRESS_KEYS = ["from", "to", "url"] as const;

const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
	const { data } = breadcrumb;
	for (const key of ADDRESS_KEYS) {
		const address = data?.[key];
		if (data && typeof address === "string") {
			data[key] = withoutSetPasswordToken(address);
		}
	}
	return breadcrumb;
};

const scrubEvent = (event: Event): Event => {
	if (event.request?.url) {
		event.request.url = withoutSetPasswordToken(event.request.url);
	}
	for (const span of event.spans ?? []) {
		if (span.description) {
			span.description = withoutSetPasswordToken(span.description);
		}
	}
	// A replay's list of the addresses it visited.
	const { urls } = event as { urls?: unknown };
	if (Array.isArray(urls)) {
		(event as { urls: unknown[] }).urls = urls.map((address) =>
			typeof address === "string" ? withoutSetPasswordToken(address) : address
		);
	}
	return event;
};

/** rrweb's events that carry an address: the page's record, and replay's own spans. */
const RRWEB_META = 4;
const RRWEB_CUSTOM = 5;

const scrubRecordingEvent = <T extends { type: number; data?: unknown }>(
	event: T
): T => {
	const data = event.data as Record<string, unknown> | undefined;
	if (event.type === RRWEB_META && typeof data?.href === "string") {
		data.href = withoutSetPasswordToken(data.href);
	}
	if (event.type === RRWEB_CUSTOM && data?.tag === "performanceSpan") {
		const span = data.payload as {
			description?: unknown;
			data?: Record<string, unknown>;
		};
		if (typeof span.description === "string") {
			span.description = withoutSetPasswordToken(span.description);
		}
		if (typeof span.data?.previous === "string") {
			span.data.previous = withoutSetPasswordToken(span.data.previous);
		}
	}
	return event;
};

// biome-ignore lint/suspicious/noExplicitAny: router instance type varies
export function initSentry(router: any, config: SentryConfig) {
	if (!SENTRY_ENABLED) return;

	init({
		dsn: config.dsn,
		sendDefaultPii: true,
		tunnel: config.tunnel,
		environment: SENTRY_ENVIRONMENT,
		tracesSampleRate: 1.0,
		ignoreErrors: [
			// ELASO-1H / ELASO-HR: Browser cancels fetch on navigation/tab close or media stream abort
			"AbortError",
			// ELASO-8E: iOS Safari kills IndexedDB when recycling background tabs
			"IDBDatabase",
			"IndexedDB",
			// ELASO-G: User denying camera/mic/screen share permission prompt
			"NotAllowedError",
			"Permission denied",
			// ELASO-HV: getDisplayMedia not supported on mobile/older browsers
			"DeviceUnsupportedError",
			"getDisplayMedia not supported",
		],
		enableLogs: true,
		integrations: [
			tanstackRouterBrowserTracingIntegration(router),
			replayIntegration({ beforeAddRecordingEvent: scrubRecordingEvent }),
			feedbackIntegration({
				colorScheme: "system",
				autoInject: false,
				enableScreenshot: true,
			}),
			consoleLoggingIntegration({ levels: ["warn", "error"] }),
		],
		replaysSessionSampleRate: 1.0,
		replaysOnErrorSampleRate: 1.0,
		beforeBreadcrumb: scrubBreadcrumb,
		beforeSend(event) {
			const dominated = event.exception?.values?.some(
				(ex) => ex.mechanism?.handled === false
			);
			if (dominated && event.event_id) {
				showReportDialog({ eventId: event.event_id });
			}
			return event;
		},
	});
	// Errors, traces and replays alike.
	addEventProcessor(scrubEvent);
}

export { logger as sentryLogger } from "@sentry/react";

export const sentryCreateRootOptions = SENTRY_ENABLED
	? {
			onUncaughtError: reactErrorHandler(),
			onCaughtError: reactErrorHandler(),
			onRecoverableError: reactErrorHandler(),
		}
	: {};
